import { useCallback, useEffect, useRef, useState } from 'react';
import { t } from '../../../i18n';
import {
  UnverifiedDraftError,
  type ApplyResult,
  type AutoScheduleEngine,
  type ScheduleDraft,
  type ScheduleOptions,
} from '../../../services/autoSchedule';

export type AutoScheduleStep = 'setup' | 'drafting' | 'review' | 'saving' | 'done' | 'error';

/** The database refused a plan at save time: the roster moved on since it was drafted. */
const REFUSED_ON_SAVE = 'ASC02';
/** Postgres gave up on the request before it finished. Nothing was written. */
const TIMED_OUT = '57014';

function describeError(error: unknown, stage: 'draft' | 'save'): string {
  if (error instanceof UnverifiedDraftError) {
    return t('The draft could not be checked clean against the roster rules, so nothing is proposed. Try a shorter period, or fix the breaches already on the board first.');
  }
  if ((error as { code?: string } | null)?.code === TIMED_OUT) {
    return t('That was too much to check in one go, so nothing was saved. Choose fewer sites or a shorter period and try again.');
  }
  if (stage === 'save' && (error as { code?: string } | null)?.code === REFUSED_ON_SAVE) {
    return t('The roster changed while you were reviewing, and this draft no longer fits the rules. Nothing was saved. Draft it again.');
  }
  return (error as Error | null)?.message || (stage === 'draft'
    ? t('The schedule could not be drafted.')
    : t('The schedule could not be saved.'));
}

/**
 * The dialog's flow: options, then a verified draft to review, then a save.
 * Nothing is written before `save`. A draft that comes back after the dialog
 * has moved on (or closed) is dropped.
 */
export function useAutoSchedule(engine: AutoScheduleEngine) {
  const [step, setStep] = useState<AutoScheduleStep>('setup');
  const [draft, setDraft] = useState<ScheduleDraft | null>(null);
  const [result, setResult] = useState<ApplyResult | null>(null);
  const [error, setError] = useState('');
  const requestRef = useRef(0);

  useEffect(() => () => { requestRef.current += 1; }, []);

  const makeDraft = useCallback(async (siteIds: string[], options: ScheduleOptions) => {
    const request = requestRef.current + 1;
    requestRef.current = request;
    setStep('drafting');
    setError('');
    setDraft(null);
    try {
      const next = await engine.draft(siteIds, options);
      if (requestRef.current !== request) return;
      setDraft(next);
      setStep('review');
    } catch (err) {
      if (requestRef.current !== request) return;
      setError(describeError(err, 'draft'));
      setStep('error');
    }
  }, [engine]);

  const save = useCallback(async (): Promise<ApplyResult | null> => {
    if (!draft) return null;
    const request = requestRef.current + 1;
    requestRef.current = request;
    setStep('saving');
    setError('');
    try {
      const saved = await engine.apply(draft);
      if (requestRef.current !== request) return null;
      setResult(saved);
      setStep('done');
      return saved;
    } catch (err) {
      if (requestRef.current !== request) return null;
      setError(describeError(err, 'save'));
      setStep('review');
      return null;
    }
  }, [draft, engine]);

  const backToSetup = useCallback(() => {
    requestRef.current += 1;
    setError('');
    setStep('setup');
  }, []);

  return { step, draft, result, error, makeDraft, save, backToSetup };
}
